<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import {
  messageSchema, MessageMarkdownTransformer, MessageMarkdownSerializer,
  EditorState, EditorView, buildEditor,
} from '@chatwoot/prosemirror-schema';

const props = defineProps({
  modelValue: { type: String, default: '' },
  editorId: { type: String, required: true },
  placeholder: { type: String, default: '' },
  disabled: Boolean,
  handleSubmitShortcut: { type: Boolean, default: true },
});
const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'submit']);
const root = ref(null);
let view;
const parser = new MessageMarkdownTransformer(messageSchema);
const createState = value => EditorState.create({
  doc: parser.parse(value || ''),
  plugins: buildEditor({ schema: messageSchema, placeholder: props.placeholder, enabledMenuOptions: [] }),
});

onMounted(() => {
  view = new EditorView(root.value, {
    state: createState(props.modelValue),
    editable: () => !props.disabled,
    attributes: { role: 'textbox', 'aria-label': '译文预览，可编辑', 'aria-multiline': 'true', 'aria-disabled': String(props.disabled), dir: 'auto' },
    dispatchTransaction(transaction) {
      if (props.disabled && transaction.docChanged) return;
      view.updateState(view.state.apply(transaction));
      if (transaction.docChanged) emit('update:modelValue', MessageMarkdownSerializer.serialize(view.state.doc));
    },
    handleDOMEvents: {
      focus() { emit('focus'); return false; },
      blur() { emit('blur'); return false; },
      keydown(_view, event) {
        if (event.key === 'Enter' && (event.isComposing || _view.composing)) { event.stopPropagation(); return true; }
        if (props.handleSubmitShortcut && event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !event.isComposing) {
          event.preventDefault(); event.stopPropagation();
          if (!props.disabled) emit('submit');
          return true;
        }
        return false;
      },
      click(_view, event) {
        if (event.target.closest?.('a')) { event.preventDefault(); return true; }
        return false;
      },
    },
  });
});

watch(() => props.modelValue, value => {
  if (!view) return;
  const doc = parser.parse(value || '');
  // Avoid replacing the document/selection on v-model echoes or equivalent Markdown.
  if (!doc.eq(view.state.doc)) view.updateState(createState(value));
});
watch(() => props.editorId, () => {
  if (view) view.updateState(createState(props.modelValue));
});
watch(() => props.disabled, disabled => {
  view?.setProps({ editable: () => !disabled, attributes: { role: 'textbox', 'aria-label': '译文预览，可编辑', 'aria-multiline': 'true', 'aria-disabled': String(disabled), dir: 'auto' } });
});
function insertPlainText(text) {
  if (props.disabled || !view || !text) return;
  view.dispatch(view.state.tr.insertText(text).scrollIntoView()); view.focus();
}
defineExpose({ insertPlainText });
onBeforeUnmount(() => { view?.destroy(); view = null; });
</script>

<template>
  <div ref="root" class="bcw-translation-editor" :class="{ 'is-disabled': disabled }" />
</template>

<style scoped>
.bcw-translation-editor { flex:1 1 0; min-width:0; min-height:0; overflow-y:auto; overscroll-behavior:contain; overflow-wrap:anywhere; }
.bcw-translation-editor :deep(.ProseMirror) { min-height:100%; padding:0; margin:0; border:0; border-radius:0; outline:none; box-shadow:none; background:transparent; font:inherit; font-size:14px; line-height:1.6; white-space:pre-wrap; }
.bcw-translation-editor :deep(.ProseMirror:focus), .bcw-translation-editor :deep(.ProseMirror:focus-visible) { outline:none; box-shadow:none; }
.bcw-translation-editor :deep(p) { margin:0 0 .5em; }
.bcw-translation-editor :deep(p:last-child) { margin-bottom:0; }
.bcw-translation-editor :deep(ul) { list-style:disc; padding-inline-start:1.5em; }
.bcw-translation-editor :deep(ol) { list-style:decimal; padding-inline-start:1.5em; }
.bcw-translation-editor :deep(a) { color:var(--color-blue-9,#2563eb); text-decoration:underline; }
.bcw-translation-editor :deep(blockquote) { border-inline-start:2px solid #cbd5e1; padding-inline-start:.75em; }
.bcw-translation-editor :deep(pre) { white-space:pre-wrap; }
.bcw-translation-editor.is-disabled { opacity:.6; }
</style>
